export const AR_STATUS = {
  PREVIEW: 'preview',
  ISSUED: 'issued',
  QUERIED: 'queried',
  AGREED: 'agreed',
  LOCKED: 'locked',
} as const;

export type ArStatus = (typeof AR_STATUS)[keyof typeof AR_STATUS];

export const AR_STATUS_LABELS = {
  [AR_STATUS.PREVIEW]: 'Preview',
  [AR_STATUS.ISSUED]: 'Issued',
  [AR_STATUS.QUERIED]: 'Queried',
  [AR_STATUS.AGREED]: 'Agreed',
  [AR_STATUS.LOCKED]: 'Locked',
} as const satisfies Record<ArStatus, string>;

/**
 * Whether members may still be approved on a return in this status.
 *
 * Only Issued and Queried qualify. Preview comes before there is anything to
 * check, and once the return is Agreed or Locked the roll is settled. This
 * mirrors `AnnualReturnStatus::isNegotiable()` on the API, which is what
 * refuses the approve endpoints, so the interface must not offer an approve
 * action outside these two.
 */
export function isApprovableStatus(status: string | undefined | null): boolean {
  return status === AR_STATUS.ISSUED || status === AR_STATUS.QUERIED;
}

export const AR_STATUS_OPTIONS = [
  { label: AR_STATUS_LABELS[AR_STATUS.PREVIEW], value: AR_STATUS.PREVIEW },
  { label: AR_STATUS_LABELS[AR_STATUS.ISSUED], value: AR_STATUS.ISSUED },
  { label: AR_STATUS_LABELS[AR_STATUS.QUERIED], value: AR_STATUS.QUERIED },
  { label: AR_STATUS_LABELS[AR_STATUS.AGREED], value: AR_STATUS.AGREED },
  { label: AR_STATUS_LABELS[AR_STATUS.LOCKED], value: AR_STATUS.LOCKED },
] as const;

/**
 * Display configuration for AR status chips.
 *
 * Lifecycle: preview → issued → agreed → locked
 *                        ↓         ↑
 *                     queried → issued
 */
export const AR_STATUS_DISPLAY = {
  [AR_STATUS.PREVIEW]: {
    color: 'blue-grey-6',
    outline: false,
    icon: 'visibility',
  },
  [AR_STATUS.ISSUED]: {
    color: 'info',
    outline: false,
    icon: 'mark_email_read',
  },
  [AR_STATUS.QUERIED]: { color: 'orange-8', outline: false, icon: 'help' },
  [AR_STATUS.AGREED]: {
    color: 'positive',
    outline: false,
    icon: 'check_circle',
  },
  [AR_STATUS.LOCKED]: { color: 'dark', outline: true, icon: 'lock' },
} as const satisfies Record<
  ArStatus,
  { color: string; outline: boolean; icon: string }
>;

export function resolveArStatus(statusValue: string | null | undefined): {
  color: string;
  outline: boolean;
  icon: string;
} {
  if (!statusValue) return { color: 'grey', outline: false, icon: 'info' };
  const display = AR_STATUS_DISPLAY[statusValue as ArStatus];
  return display ?? { color: 'grey', outline: false, icon: 'info' };
}

/**
 * Per-status count map supplied by the API under `meta.status_counts`.
 * Keyed by the five real AR statuses.
 */
export type ArStatusCounts = Record<ArStatus, number>;

/**
 * Build a zeroed counts map. Used as the store's initial value and as a
 * defensive fallback when the API response omits `status_counts`.
 */
export function createEmptyStatusCounts(): ArStatusCounts {
  return {
    [AR_STATUS.PREVIEW]: 0,
    [AR_STATUS.ISSUED]: 0,
    [AR_STATUS.QUERIED]: 0,
    [AR_STATUS.AGREED]: 0,
    [AR_STATUS.LOCKED]: 0,
  };
}
